import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, ApiError } from "../lib/api";
import { useConsent, useBillingStatus, useFamilyBilling, queryKeys } from "../lib/queries";
import { PlanChooser } from "../components/PlanChooser";
import { PlanRequestStatus } from "../components/PlanRequestStatus";
import { StopTraining } from "../components/StopTraining";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { SettingsTabs } from "../components/SettingsTabs";
import { ThemeToggle } from "../components/ThemeToggle";
import { WeightUnitSetting } from "../components/WeightUnitSetting";
import { ChangePasswordForm } from "../components/ChangePasswordForm";
import { DataRights } from "../components/DataRights";

interface ConsentStatus {
  privacy_policy_accepted_at: string | null;
  tos_accepted_at: string | null;
  parental_consent_accepted_at: string | null;
}

interface BillingDetails {
  /** How many athletes the live subscription is billing for. The per-athlete detail is separate. */
  billedQuantity?: number | null;
  billingStatus: "pending" | "active" | "past_due" | "cancelled" | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
}

/**
 * The day something was agreed to, as a person would say it.
 *
 * This was `toLocaleString()`, so the first thing on the settings page was three identical timestamps
 * to the second -- "8/7/2026, 11:43:22 PM" three times over -- which reads as a log file rather than as
 * proof of anything (UX review F26). If the point is that a person can see what they agreed to, the
 * second it was recorded is noise and the date is the fact.
 */
function formatTimestamp(value: string | null): string {
  if (!value) return "Not yet accepted";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/** The day a queued change lands, in the reader's own locale. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

/** Cents are how money is stored; dollars are how it is read. */
function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * One date format for the whole app, with the month spelled out.
 *
 * This was `toLocaleDateString()`, so the same card carried "Access ends: 9/7/2026" and "moving to
 * Maintenance on September 7" a centimetre apart -- and 9/7 is September 7th or July 9th depending on
 * where the reader lives, on a line about when their access stops (UX review F35/F24).
 */
function formatUnixDate(seconds: number | null): string {
  if (!seconds) return "unknown";
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** The database's word for a state is not a person's word for it (F24). */
const BILLING_STATUS_LABEL: Record<string, string> = {
  active: "Your subscription is active",
  pending: "Waiting for your first payment",
  past_due: "A payment didn't go through",
  cancelled: "Your subscription has ended",
};

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Guardian/adult-self account settings (PRD §4.3, §10): when consent was accepted, current billing
 * status/next billing date, manage payment method + invoice history (via Stripe's hosted Customer
 * Portal -- no reason to hand-roll UI Stripe already ships), and cancel-with-confirmation.
 *
 * Deliberately doesn't try to detect/hide this page from a minor's own login client-side -- billing and
 * consent are enforced server-side (both endpoints 403 without an accountId, PRD §14.2's "enforced at
 * the API layer, not just hidden in the UI"), so a minor landing here just sees the "not available" state.
 */
export function AccountSettings() {
  const queryClient = useQueryClient();
  const consentQuery = useConsent<ConsentStatus>();
  const billingQuery = useBillingStatus<BillingDetails>();
  // What the monthly figure is actually made of -- separate from the Stripe status above it.
  const familyBilling = useFamilyBilling();

  const [confirmingCancel, setConfirmingCancel] = useState(false);
  /** Which athlete's plan chooser is open. One at a time -- three open lists is a wall of tiles. */
  const [choosingFor, setChoosingFor] = useState<string | null>(null);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);

  const cancelSubscription = useMutation({
    mutationFn: () => apiPost<{ currentPeriodEnd: number | null }>("/billing/cancel-subscription"),
    onSuccess: async ({ currentPeriodEnd }) => {
      setCancelledMessage(
        `Your subscription will end on ${formatUnixDate(currentPeriodEnd)}. You'll keep full access until then.`
      );
      setConfirmingCancel(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.billingStatus });
    },
  });

  /**
   * Changing your mind (UX review F34).
   *
   * After confirming a cancellation the button simply disappeared and nothing replaced it, so a family
   * who cancelled by mistake had no route back short of contacting somebody. That is the one moment a
   * product should make it easiest to stay.
   */
  const resumeSubscription = useMutation({
    mutationFn: () => apiPost<{ currentPeriodEnd: number | null }>("/billing/resume-subscription"),
    onSuccess: async () => {
      setCancelledMessage(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.billingStatus });
    },
  });

  const openPortal = useMutation({
    mutationFn: () => apiPost<{ url: string }>("/billing/portal-session"),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  // A 403 on either endpoint is the documented "this login has no account row" case (a minor's own
  // login), not a failure worth showing as an error.
  if (isForbidden(consentQuery.error) || isForbidden(billingQuery.error)) {
    return (
      <div className="dashboard">
        <p className="empty-state">Account settings aren't available for this login.</p>
        <Link className="link-button" to="/family">
          Back
        </Link>
      </div>
    );
  }

  const consent = consentQuery.data;
  const billing = billingQuery.data;
  const isPending = consentQuery.isPending || billingQuery.isPending;
  const loadError = consentQuery.error ?? billingQuery.error;
  const actionError = cancelSubscription.error ?? openPortal.error ?? resumeSubscription.error;
  /** The amount to put beside the date, so "next payment" is a figure rather than just a day (F24). */
  const monthlyTotal =
    familyBilling.data?.monthlyTotalCents != null ? formatCents(familyBilling.data.monthlyTotalCents) : null;

  const billingTabContent = (
    <>
      <h2>Billing</h2>
      {billing && (
        <div className="plan-form">
          {/* The raw enum in a code-styled chip -- "active", "past_due" -- is the database's word for
              this, not a person's. Said in a sentence instead (UX review F24). */}
          <p>
            <span className={`badge ${billing.billingStatus === "active" ? "badge-ok" : "badge-warn"}`}>
              {BILLING_STATUS_LABEL[billing.billingStatus ?? "pending"]}
            </span>
          </p>
          {billing.currentPeriodEnd && (
            <p>
              {billing.cancelAtPeriodEnd ? (
                <>Your access ends on <strong>{formatUnixDate(billing.currentPeriodEnd)}</strong>.</>
              ) : (
                <>
                  Your next payment is{" "}
                  {monthlyTotal && <strong>{monthlyTotal} </strong>}
                  on <strong>{formatUnixDate(billing.currentPeriodEnd)}</strong>.
                </>
              )}
            </p>
          )}
          {cancelledMessage && <p>{cancelledMessage}</p>}

          {/*
            The way back, while there is still time to take it (UX review F34).

            Also the answer to F33: once a cancellation is scheduled it supersedes any pending plan
            change, and saying so here is what stops the card asserting three contradictory things at
            once -- a downgrade landing on the 7th, a subscription ending on the 7th, and a status of
            "active" -- with no indication of which one wins.
          */}
          {billing.cancelAtPeriodEnd && (
            <div className="confirm-box" role="group" aria-label="Your subscription is ending">
              <p>
                Any plan change you had scheduled won&rsquo;t happen now &mdash; the subscription ends
                first. You can still change your mind.
              </p>
              <button
                type="button"
                disabled={resumeSubscription.isPending}
                onClick={() => resumeSubscription.mutate()}
              >
                {resumeSubscription.isPending ? "Restarting…" : "Keep my subscription"}
              </button>
            </div>
          )}

          <button onClick={() => openPortal.mutate()} disabled={openPortal.isPending}>
            {openPortal.isPending ? "Opening…" : "Manage payment method & invoices"}
          </button>

          {/*
            What the number is made of, athlete by athlete (finding #15). A family with two skaters is
            billed for two, the second discounted -- and a total that appears for the first time on an
            invoice, with no breakdown anywhere, is a support ticket waiting to happen.
          */}
          {familyBilling.data && familyBilling.data.athletes.length > 0 && (
            <div className="pricing-preview">
              <p className="form-label">What you&rsquo;re paying for</p>
              <ul>
                {familyBilling.data.athletes.map((athlete) => (
                  <li key={athlete.client_id}>
                    {athlete.stopped
                      ? `${athlete.name}: not training — not billed`
                      : `${athlete.name}: ${athlete.plan_name ?? "no plan yet"}${
                          athlete.plan_price_cents !== null ? ` — ${formatCents(athlete.plan_price_cents)}/mo` : ""
                        }`}
                    {athlete.stopping_at && !athlete.stopped && (
                      <> <span className="badge badge-warn">{`stopping on ${formatDate(athlete.stopping_at)}`}</span></>
                    )}
                    {athlete.pending_plan_name && (
                      <>
                        {" "}
                        <span className="badge badge-warn">
                          moving to {athlete.pending_plan_name}
                          {athlete.pending_effective_at ? ` on ${formatDate(athlete.pending_effective_at)}` : ""}
                        </span>
                      </>
                    )}
                  </li>
                ))}
                {familyBilling.data.monthlyTotalCents !== null && (
                  <li>
                    <strong>{formatCents(familyBilling.data.monthlyTotalCents)} a month</strong>
                    {/* Only where there IS a second athlete. With one skater this read "additional
                        athletes are 25% off" under a single line item, which is an advert on an
                        invoice -- and it never showed its working either way (UX review F25). */}
                    {familyBilling.data.siblingDiscountPercent && familyBilling.data.athletes.length > 1
                      ? ` — includes ${familyBilling.data.siblingDiscountPercent}% off for each additional athlete`
                      : ""}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Choosing a plan, per athlete. The family's own control -- the coach can do the same from
              their side, and both go through the same rules about when a change takes effect. */}
          {familyBilling.data?.athletes.map((athlete) => (
            <div key={athlete.client_id} className="plan-choice-block">
              {/* Outside the disclosure on purpose: a request waiting, or the coach's answer to one, is
                  what the family needs to see without going looking (27 Sep debug notes item 12). */}
              <PlanRequestStatus athlete={athlete} />
              {/* A button rather than a <details>: the disclosure state is ours to control, and a
                  native <details> is one more thing that behaves differently across browsers. */}
              <button
                type="button"
                className="chip"
                aria-expanded={choosingFor === athlete.client_id}
                onClick={() => setChoosingFor(choosingFor === athlete.client_id ? null : athlete.client_id)}
              >
                {athlete.stopped ? `${athlete.name}'s training` : `Change ${athlete.name}'s plan`}
              </button>
              {choosingFor === athlete.client_id && (
                <>
                  {/* A stopped athlete has no plan to pick; what they need is the way back. */}
                  {!athlete.stopped && <PlanChooser athlete={athlete} onChanged={() => setChoosingFor(null)} />}
                  {/* Stopping one child, without cancelling the subscription their sibling is on. */}
                  <StopTraining athlete={athlete} />
                </>
              )}
            </div>
          ))}

          {billing.billingStatus === "active" && !billing.cancelAtPeriodEnd && (
            <>
              {/* Quiet, and last. "Cancel subscription" was the most prominent control in the billing
                  card -- full-width and heavy -- while "Change your plan" was a small grey chip, so the
                  loudest thing offered to somebody working out what they can afford was leaving
                  (UX review F27). The plan choices above are the visible option now; this is the one
                  you have to mean. */}
              {!confirmingCancel && (
                <button type="button" className="link-button quiet" onClick={() => setConfirmingCancel(true)}>
                  {/* Named by what it actually ends. Beside "Stop <name>'s training" this read as the
                      same kind of action at the same scale, and it is not (F31). */}
                  Cancel everything and end my subscription
                </button>
              )}
              {confirmingCancel && (
                <div className="plan-form">
                  <p>
                    Are you sure? You'll keep access through the end of your current paid period, then your subscription ends.
                  </p>
                  <button type="button" onClick={() => cancelSubscription.mutate()} disabled={cancelSubscription.isPending}>
                    {cancelSubscription.isPending ? "Cancelling…" : "Yes, cancel my subscription"}
                  </button>

                  {/*
                    An alternative to leaving, offered at the moment of leaving. Cancelling ends the
                    relationship; moving to a cheaper plan keeps it, and which cheaper plans exist is
                    the coach's catalogue rather than a fixed tier this page knows about.
                  */}
                  <p className="form-hint">
                    Taking a break rather than leaving? Changing an athlete to a cheaper plan above keeps
                    their plans, their history and their coach.
                  </p>

                  <button type="button" className="chip" onClick={() => setConfirmingCancel(false)}>
                    Never mind
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );

  /* Consent, password and preferences, grouped under one "Account" tab. These were previously the
     middle of one long scroll -- Consent -> Password -> Units -> Appearance -> Your data -- underneath
     Billing, which is the one thing a family actually opens this page for (UX review F23). The tabs
     this page now has (27 Sep debug notes item 13) replace that ordering trick: Billing is simply its
     own tab and the default one, rather than winning a fight for the top of a single scroll. */
  const accountTabContent = (
    <>
      <h2>Consent</h2>
      {consent && (
        /* Each document links to itself. Proof of consent that doesn't let you re-read what you
           agreed to isn't proof of much, and this is the only place either document is referenced
           after signup (F26). */
        <ul className="goal-list">
          <li>
            You agreed to the <Link to="/privacy">Privacy Policy</Link> on{" "}
            {formatTimestamp(consent.privacy_policy_accepted_at)}
          </li>
          <li>
            You agreed to the <Link to="/terms">Terms of Service</Link> on{" "}
            {formatTimestamp(consent.tos_accepted_at)}
          </li>
          {consent.parental_consent_accepted_at && (
            <li>
              You gave parental consent for your athlete on{" "}
              {formatTimestamp(consent.parental_consent_accepted_at)}
            </li>
          )}
        </ul>
      )}

      {/* Shared with the coach's settings page. The only password change the app had was the emailed
          forgot-password code, so rotating a password meant pretending to have forgotten it. */}
      <h2>Password</h2>
      <ChangePasswordForm />

      <WeightUnitSetting />
    </>
  );

  const appearanceTabContent = (
    <>
      <h2>Appearance</h2>
      <ThemeToggle />
    </>
  );

  return (
    <PageShell
      title="Account settings"
      actions={
        <Link className="link-button" to="/family">
          Back
        </Link>
      }
    >
      {actionError && <ErrorState error={actionError} />}
      {loadError && (
        <ErrorState
          error={loadError}
          onRetry={() => {
            consentQuery.refetch();
            billingQuery.refetch();
          }}
        />
      )}

      {isPending && <LoadingState label="Loading your account…" />}

      <SettingsTabs
        defaultTabId="billing"
        tabs={[
          { id: "billing", label: "Billing", content: billingTabContent },
          { id: "account", label: "Account", content: accountTabContent },
          { id: "appearance", label: "Appearance", content: appearanceTabContent },
          {
            id: "your-data",
            label: "Your data",
            // Last, and on its own (UX review F23): deletion is irreversible, and the tabs don't change
            // that -- it still isn't anywhere near the settings people actually came for.
            //
            // Still withheld while the account state is unknown: with the connection dead this rendered
            // its "Delete my account" button above a billing and consent section that had never loaded,
            // offering the most destructive action in the product while unable to say what the account
            // even is (F89).
            content: !isPending && !loadError ? <DataRights canDelete /> : null,
          },
        ]}
      />
    </PageShell>
  );
}
